/**
 * Light/dark, as a class on `<html>`.
 *
 * A class rather than only `prefers-color-scheme`, for two reasons: a user
 * can override their operating system, and a test can assert both themes
 * without emulating a media query. The stored preference wins; "system"
 * means follow the OS and keep following it if the OS changes mid-session.
 */

export type Theme = "light" | "dark" | "system";

const STORAGE_KEY = "mdm.theme";

export function storedTheme(): Theme {
  const value = globalThis.localStorage?.getItem(STORAGE_KEY);
  return value === "light" || value === "dark" || value === "system" ? value : "system";
}

export function prefersDark(): boolean {
  return globalThis.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false;
}

export function applyTheme(theme: Theme): void {
  const dark = theme === "dark" || (theme === "system" && prefersDark());
  document.documentElement.classList.toggle("dark", dark);
  // Tells form controls, scrollbars and the browser's own UI which way
  // round the page is. Without it a dark page gets light native widgets.
  document.documentElement.style.colorScheme = dark ? "dark" : "light";
}

export function setTheme(theme: Theme): void {
  globalThis.localStorage?.setItem(STORAGE_KEY, theme);
  applyTheme(theme);
}

/**
 * Apply the stored theme and keep following the OS while it is "system".
 * Returns an unsubscribe so a test can tear the listener down.
 */
export function startTheme(): () => void {
  applyTheme(storedTheme());
  const media = globalThis.matchMedia?.("(prefers-color-scheme: dark)");
  if (!media) return () => {};
  const onChange = (): void => {
    if (storedTheme() === "system") applyTheme("system");
  };
  media.addEventListener("change", onChange);
  return () => media.removeEventListener("change", onChange);
}
